<template>
  <div class="flex mt-5 md:space-x-4" v-if="property">
    <div class="mb-10 md:w-[50rem] w-full">
      <div class="flex flex-col w-full md:flex-row">
        <h3 class="flex items-center text-xl font-bold capitalize md:text-2xl">
          {{ property.title }} from
          {{ formattedAmount(Number(property.price)) }}
        </h3>
      </div>
      <div class="grid grid-cols-2 gap-5 mt-5">
        <div
          class="flex flex-col items-center justify-center p-5 rounded-md bg-gray-50"
        >
          <img
            src="@/assets/icons/estate-developer.svg"
            class="w-10 h-10"
            alt=""
          />
          <h5 class="mt-2 text-xl text-stone-600">Developer</h5>
          <h4 class="pt-2 font-bold text-center md:text-2xl text-stone-900">
            {{ property.company.name }}
          </h4>
        </div>
        <div
          class="flex flex-col items-center justify-center p-5 rounded-md bg-gray-50"
          v-if="property.delivery_date"
        >
          <img src="@/assets/icons/construction.svg" class="w-10 h-10" alt="" />
          <h5 class="mt-2 text-xl text-stone-600">Delivery Date</h5>
          <h4 class="pt-2 font-bold text-center md:text-2xl text-stone-900">
            {{ getQuarterWithYear(property.delivery_date) }}
          </h4>
        </div>
      </div>
      <div class="flex flex-col mt-5 md:hidden">
        <p class="font-semibold text-center">
          Get the latest information about this project.
        </p>
        <div class="flex justify-between mt-4 space-x-3">
          <a
            href="#"
            target="__blank"
            @click.prevent="handleContactClick('email')"
            class="text-primary cursor-pointer rounded-lg hover:transition duration-200 ease-in-out w-full px-10 h-[48px] hover:bg-[#E3E1F2] flex items-center font-semibold text-sm justify-center border border-primary"
          >
            <EmailSvg color="primary" height="4" width="4" />
            <span class="ml-1">Email</span>
          </a>
          <a
            href="#"
            @click.prevent="handleContactClick('whatsapp')"
            target="__blank__"
            class="text-white cursor-pointer rounded-lg hover:transition duration-200 ease-in-out w-full px-7 h-[48px] bg-secondary hover:bg-red-500 flex items-center font-semibold text-sm justify-center"
          >
            <WhatsAppSvg color="white" height="4" width="4" />
            <span class="ml-1">WhatsApp</span>
          </a>
        </div>
      </div>

      <hr class="my-8" />
      <div>
        <h1>Description</h1>
        <div>
          <div v-if="!showFullDescription">
            <p
              class="text-gray-500 line-clamp-[9]"
              v-html="shortDescription"
            ></p>
            <button
              class="flex items-center px-2 py-1 mt-1 text-sm font-semibold text-black border border-gray-200 rounded"
              @click="toggleDescription"
            >
              <DropdownIconSvg
                class="stroke-2 ms-0 text-secondary"
                color="secondary"
                height="4"
                width="4"
                :class="{ 'rotate-180': showFullDescription }"
              />
              <span class="pl-1">Read more</span>
            </button>
          </div>
          <div v-else>
            <p
              class="font-light text-gray-500"
              v-html="property.description"
            ></p>
            <button
              class="flex items-center px-2 py-1 mt-5 text-sm font-semibold text-black border border-gray-200 rounded"
              @click="showFullDescription = false"
            >
              <DropdownIconSvg
                class="stroke-2 ms-0 text-secondary"
                color="secondary"
                height="4"
                width="4"
                :class="{ 'rotate-180': shortDescription }"
              />
              <span class="pl-1">Read less</span>
            </button>
          </div>
        </div>
      </div>
      <hr class="my-8" />
      <h1 class="pb-8 text-xl font-semibold">Payment plan</h1>
      <div
        class="md:flex w-full md:w-[50rem] md:space-x-4 md:space-y-0 space-y-5 mt-8 md:justify-between"
      >
        <div
          v-for="(plan, index) in property.payment_plans"
          :key="plan.id"
          class="flex items-center"
        >
          <div
            class="flex flex-col items-center justify-center w-full p-4 rounded-lg md:mr-4 md:w-56 bg-gray-50 h-28"
          >
            <p class="text-xl font-medium">{{ plan.percentage }}%</p>
            <p class="mt-3 text-sm text-stone-600">{{ plan.description }}</p>
          </div>
          <svg
            v-if="index < property.payment_plans.length - 1"
            width="24"
            height="24"
            class="hidden md:flex"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M8.14645 4.64645C8.32001 4.47288 8.58944 4.4536 8.78431 4.58859L8.85355 4.64645L15.8536 11.6464C16.0271 11.82 16.0464 12.0894 15.9114 12.2843L15.8536 12.3536L8.85355 19.3536C8.65829 19.5488 8.34171 19.5488 8.14645 19.3536C7.97288 19.18 7.9536 18.9106 8.08859 18.7157L8.14645 18.6464L14.793 12L8.14645 5.35355C7.97288 5.17999 7.9536 4.91056 8.08859 4.71569L8.14645 4.64645Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      </div>
      <hr class="my-8" />
      <h1 class="pb-8 text-xl font-semibold">Available Units</h1>
      <div v-for="(units, type) in groupedUnits" :key="type">
        <h3 class="pb-2 font-medium uppercase text-stone-400">{{ type }}</h3>
        <div class="my-2 space-y-2 md:w-[50rem]">
          <div
            v-for="unit in units"
            :key="unit.id"
            class="flex flex-col gap-2 p-5 text-black bg-gray-100 rounded-lg group"
            :tabindex="unit.id"
          >
            <div
              class="flex items-center justify-between w-full cursor-pointer"
            >
              <span class="font-medium"> {{ unit.number_of_beds }} Beds </span>
              <div class="flex items-center space-x-3">
                <div class="flex items-center space-x-2">
                  <PropertySizeSvg color="black" height="4" width="4" />
                  <p class="">{{ unit.property_size }}</p>
                </div>
                <img
                  src="@/assets/icons/down-arrow.svg"
                  alt="size"
                  class="w-5 h-5 transition-all duration-500 group-focus:-rotate-180"
                />
              </div>
            </div>
            <div
              class="items-center invisible h-auto transition-all opacity-0 max-h-0 group-focus:visible group-focus:max-h-screen group-focus:opacity-100 group-focus:duration-1000"
            ></div>
          </div>
        </div>
      </div>

      <hr class="my-8" />
      <div class="">
        <h1 class="pb-8 text-xl font-semibold">Location</h1>
        <p class="mt-10 mb-3 font-light">{{ property.address }}</p>
        <div id="map" class="w-full h-full"></div>
      </div>
      <hr class="my-8" />

      <h1 class="pb-8 text-xl font-semibold">Amenities</h1>
      <div>
        <ul class="grid grid-cols-3 gap-3 mx-4 list-disc">
          <li v-for="amenity in property.amenities" :key="amenity.id">
            {{ amenity.name }}
          </li>
        </ul>
      </div>
    </div>
    <div class="hidden md:flex md:flex-col">
      <div
        class="sticky top-0 flex flex-col px-5 py-10 shadow-sm bg-gray-50 rounded-xl"
      >
        <p class="font-semibold text-center">
          Get the latest information about this project.
        </p>
        <div class="flex justify-between mt-4 space-x-3">
          <a
            href="#"
            target="__blank"
            @click.prevent="handleContactClick('email')"
            class="text-primary cursor-pointer rounded-lg hover:transition duration-200 ease-in-out w-auto px-10 h-[48px] hover:bg-[#E3E1F2] flex items-center font-semibold text-sm justify-center border border-primary"
          >
            <EmailSvg color="primary" height="4" width="4" />
            <span class="ml-1">Email</span>
          </a>
          <a
            href="#"
            @click.prevent="handleContactClick('whatsapp')"
            target="__blank__"
            class="text-white cursor-pointer rounded-lg hover:transition duration-200 ease-in-out w-auto px-7 h-[48px] bg-secondary hover:bg-red-500 flex items-center font-semibold text-sm justify-center"
          >
            <WhatsAppSvg color="white" height="4" width="4" />
            <span class="ml-1">WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
    <SignInModal
      :showViewModal="showLoginModal"
      :modalType="modalType"
      @closeViewModal="closeModal"
      @switchModalType="handleSwitchModalType"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, computed, watch, watchEffect, onMounted } from "vue";
import EmailSvg from "@/components/svg/EmailSvg.vue";
import WhatsAppSvg from "@/components/svg/WhatsAppSvg.vue";
import useAuth from "@/composables/useAuth";
import SignInModal from "@/components/LoginModal.vue";
import DropdownIconSvg from "@/components/svg/DropDownIconSvg.vue";
import PropertySizeSvg from "@/components/svg/PropertySizeSvg.vue";

const props = defineProps({
  property: Object,
});

const { state: authState } = useAuth();
const isAuthenticated = ref(authState.isAuthenticated);
import { useStore } from "vuex";
watch(
  () => authState.isAuthenticated,
  (newVal) => {
    isAuthenticated.value = newVal;
  }
);

const showEmailDetails = ref(null);
const showWhatsAppDetails = ref(null);
const showLoginModal = ref(false);
const modalType = ref("");
const companyDetails = ref(null);
const store = useStore();
const google_map_api_key = store.state.google_map_api_key;

watch(
  () => props.property,
  (newProperty) => {
    if (newProperty?.company) {
      companyDetails.value = newProperty.company;
    } else {
      companyDetails.value = null;
    }
  },
  { immediate: true }
);
const showFullDescription = ref(false);

const shortDescription = computed(() => {
  if (props.property?.description) {
    console.log(props.property);
    return props.property.description.substring(0, 1000);
  }
  return "";
});

const isDataReady = computed(() => {
  return isAuthenticated.value !== null && companyDetails.value !== null;
});

watchEffect(() => {
  if (isDataReady.value && isAuthenticated.value) {
    showWhatsAppDetails.value =
      "https://wa.me/" +
      companyDetails.value.phone +
      "?text=Hi, I found your new project on hutloop. Please tell me more about it.";
    showEmailDetails.value =
      "mailto:" +
      companyDetails.value.email +
      "?subject=Hutloop Property  &body=Hi, I found your property on Hutloop and i'm interested in. Please contact me. Thank you.!";
  }
});

function handleContactClick(type) {
  if (isAuthenticated.value) {
    if (type === "email") {
      window.location.href = showEmailDetails.value;
    } else if (type === "whatsapp") {
      window.location.href = showWhatsAppDetails.value;
    }
  } else {
    showLoginModal.value = true;
    modalType.value = "login";
  }
}

function toggleDescription() {
  showFullDescription.value = !showFullDescription.value;
}

function getQuarterWithYear(date) {
  const quarter = Math.floor((new Date(date).getMonth() + 3) / 3);
  const year = new Date(date).getFullYear();
  return "Q" + quarter + " " + year;
}

const closeModal = () => {
  showLoginModal.value = false;
  modalType.value = "";
};

const handleSwitchModalType = (type) => {
  modalType.value = type;
};

const groupedUnits = computed(() => {
  if (!props.property?.units) return {};
  return props.property.units.reduce((acc, unit) => {
    const type = unit.property_type.name;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(unit);
    return acc;
  }, {});
});

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve(window.google);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${google_map_api_key}&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    window.initGoogleMaps = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const initMap = (google) => {
  const mapOptions = {
    center: { lat: 0, lng: 0 },
    zoom: 20,
  };
  const map = new google.maps.Map(document.getElementById("map"), mapOptions);

  const geocoder = new google.maps.Geocoder();
  geocoder.geocode({ address: props.property.address }, (results, status) => {
    if (status === "OK") {
      map.setCenter(results[0].geometry.location);
      map.setZoom(18);

      new google.maps.Marker({
        map,
        position: results[0].geometry.location,
      });
    } else {
      console.error(
        "Geocode was not successful for the following reason:",
        status
      );
    }
  });
};

onMounted(() => {
  loadGoogleMapsScript()
    .then((google) => {
      initMap(google);
    })
    .catch((error) => {
      console.error("Error loading Google Maps:", error);
    });
});
</script>
<style scoped>
#map {
  height: 25rem;
  width: 100%;
  max-width: 50rem;
  margin: 0 auto;
}

@media (max-width: 768px) {
  #map {
    height: 20rem;
    width: 100%;
  }
}

@media (max-width: 480px) {
  #map {
    height: 15rem;
    width: 100%;
  }
}
</style>
